import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';
import { isEmptyHtml, trimTrailingEmpty, para, heading, bullets } from '../FraudAlerts/alertBodyFormat';
import { AlertPreviewModal, HotScamPreviewModal } from './AlertPreview';

// The editor (Tiptap/ProseMirror) is by far the heaviest thing on the admin
// page and only admins ever need it, so it loads as its own chunk instead of
// riding in the main bundle every visitor downloads.
const AlertEditor = lazy(() => import('./AlertEditor'));

// Contains any failure in the editor (a thrown render or effect, or its chunk
// failing to download after a deploy) to the editor's own box. Without this,
// an editor error unmounts the whole admin page: the review queue and the
// alert list vanish with it. The form's text is held by the parent, so
// retrying remounts the editor with whatever had been typed.
class EditorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('[AlertEditor] failed:', error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg min-h-[120px] px-4 py-4 text-sm text-red-800">
        <p className="font-medium">The editor ran into a problem and stopped.</p>
        <p className="mt-1">Nothing you typed is lost. Try again, or reload the page if it keeps happening.</p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="mt-3 rounded bg-white border border-red-300 px-3 py-1 text-red-700 hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    );
  }
}

// Multi-source fraud-alert authoring (Information Hub refactor).
// One form covers all four agencies; the source selector adapts the fields
// to each agency's real conventions (verified against ic3.gov / oig.ssa.gov /
// irs.gov / consumer.ftc.gov):
//   FBI/IC3 — numbered PSAs (I-MMDDYY-PSA), ic3.gov source links, PDF pre-fill
//   IRS     — optional IR-YYYY-NN release reference
//   SSA/FTC — no numbering; title + date only
// Every alert gets a required publish date (drives the New/Current flags) and
// an optional per-source reporting footer via the body templates.
//
// The body is written in a rich-text editor (AlertEditor) and saved as HTML,
// sanitized server-side. HTML is the only body format: the old markdown
// subset was converted and retired in backend migration 024. The per-agency
// templates below are built with the same para / heading / bullets shapes the
// editor produces.
//
// Nothing here publishes directly. An alert is authored as a draft, submitted
// for review, and released by an elevated (superadmin) reviewer — so the person
// writing an alert is never the only person who saw it. Edits to an already-live
// alert are staged rather than written through: the live version keeps serving
// its last approved content until the edit is approved. See migration 018.

const SOURCES = [
  {
    id: 'ic3',
    label: 'FBI / IC3',
    refLabel: 'PSA Number',
    refPlaceholder: 'I-MMDDYY-PSA (e.g. I-081325-PSA)',
    urlPlaceholder: 'https://www.ic3.gov/PSA/...',
    template: [
      para('Intro paragraph — what the FBI is warning about and how the scheme works.'),
      heading('Red Flag Indicators'),
      bullets('...'),
      heading('Protect Yourself'),
      bullets('...'),
      heading('If You Are a Victim'),
      bullets(
        'Cease all contact with the scammers and notify your financial institutions.',
        'File a complaint with the FBI Internet Crime Complaint Center at ic3.gov.',
      ),
    ].join(''),
  },
  {
    id: 'ssa',
    label: 'SSA',
    refLabel: null,
    urlPlaceholder: 'https://oig.ssa.gov/scam-alerts/...',
    template: [
      para('Opening warning paragraph — what the scam claims and who it targets.'),
      heading('How the Scam Works'),
      bullets('...'),
      heading('Common Warning Signs'),
      bullets('...'),
      heading('What You Should Do'),
      bullets('...'),
      heading('Reminder'),
      para('The SSA and SSA OIG will never:'),
      bullets(
        'Demand immediate payment or personal information',
        'Ask for gift cards, cryptocurrency, wire transfer, cash, or gold bars',
        'Threaten arrest or legal action',
      ),
      heading('Report It'),
      bullets(
        'Report Social Security-related scams to the SSA Office of the Inspector General at oig.ssa.gov/report or ssa.gov/scam.',
        'File a complaint with the FBI Internet Crime Complaint Center at ic3.gov.',
        'Report fraud to the Federal Trade Commission at ReportFraud.ftc.gov.',
      ),
    ].join(''),
  },
  {
    id: 'irs',
    label: 'IRS',
    refLabel: 'IRS Release #',
    refPlaceholder: 'IR-YYYY-NN (optional)',
    urlPlaceholder: 'https://www.irs.gov/newsroom/...',
    template: [
      para('Opening paragraph — the scheme the IRS is warning taxpayers about.'),
      heading('Warning Signs'),
      bullets('...'),
      heading('Protect Yourself and Report'),
      bullets(
        'Get tax guidance only from trusted sources: IRS.gov or a credentialed tax professional.',
        'Report abusive tax schemes to the IRS using the appropriate form (e.g. Form 14242).',
      ),
    ].join(''),
  },
  {
    id: 'ftc',
    label: 'FTC',
    refLabel: null,
    urlPlaceholder: 'https://consumer.ftc.gov/articles/...',
    template: [
      para('Opening paragraph — how the scam works.'),
      heading('How To Avoid It'),
      bullets('...'),
      heading('What To Do If You Sent Money to a Scammer'),
      bullets('...'),
      heading('Report Fraud'),
      bullets(
        'Report to the FTC at ReportFraud.ftc.gov.',
        'Contact your state Attorney General.',
      ),
    ].join(''),
  },
];

// Hot Scams (10-06, reshaped 10-07): a content page rather than an agency
// alert, authored from the same form as two free-form text boxes - one above
// the section image, one below. Headings and lists are the author's; the grey
// tiles and the fixed 02-06 lists are gone (hot-scams-tile-archive.md keeps
// the old tile text). Images are the shared hot-scam artwork until per-page
// art is supplied.
const HOT_SCAM_HERO = '/images/fraud-alerts/hot-scams/pig-butchering-hero.png';
const HOT_SCAM_KNOW_MORE = '/images/fraud-alerts/hot-scams/know-more.png';
SOURCES.push({
  id: 'hot-scam',
  label: 'ScamWire',
  refLabel: null,
  urlPlaceholder: null,
  // Box 1: the section headings the four original pages use.
  template: [
    heading('01 / What you need to know.'),
    para('What this scam is, who it targets, and the name scammers use for it.'),
    para('How it starts — the first contact (the unsolicited text, call, friend request or ad) and what the scammer is trying to learn about you.'),
    para('How the money moves — what you are asked to buy, invest in or send, how the "proof" is faked, and why the first small win is part of the plan.'),
    para('When they sense you catching on — fees, taxes, one last payment, and the moment they disappear.'),
  ].join(''),
  // Box 2, below the image.
  template2: [
    heading('02 / Red Flags'),
    bullets('The tells that this is a scam, one per line.'),
    heading('03 / Attack Mode'),
    bullets('How contact is made: calls, texts, DMs, ads, fake sites.'),
    heading('04 / Prevention Tips'),
    bullets('What to do, and what never to do.'),
    heading('05 / Detection tools'),
    bullets('Run MoSAIC PSYOPS screening.'),
    heading('06 / Av Resources'),
    bullets('Videos and podcasts in the Av Library.'),
  ].join(''),
});

const SOURCE_BY_ID = Object.fromEntries(SOURCES.map((s) => [s.id, s]));

// Row-shaped objects for the previews: what the page would render if this
// form were saved and released. Alerts (10-28) share the hot scam layout.
const alertPreviewShape = (form) => ({
  id: 'preview',
  source: form.source,
  title: form.title,
  psa_number: form.psa_number,
  published_at: form.published_at ? `${form.published_at}T00:00:00Z` : null,
  source_url: form.source_url,
  summary: form.summary,
  full_text: form.full_text,
  know_more: form.know_more,
});

// Row-shaped object for the hot scam preview: what the page would render if
// this form were saved and released.
const hotScamPreviewShape = (form) => ({
  slug: '',
  title: form.title,
  hero_caption: form.hero_caption,
  hero_image_url: HOT_SCAM_HERO,
  know_more_image_url: HOT_SCAM_KNOW_MORE,
  what_to_know: form.full_text,
  know_more: form.know_more,
});
const HOT_SCAM = 'hot-scam';

// Admin endpoints differ by kind; everything else in the flow is shared.
const adminPath = (kindOrRow) =>
  (kindOrRow === HOT_SCAM || kindOrRow?.kind === HOT_SCAM) ? 'admin/hot-scams' : 'admin/ic3/alerts';
const rowKind = (a) => (a.kind === HOT_SCAM ? HOT_SCAM : (a.source || 'ic3'));

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '');
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// What the alert would look like if its staged edit were approved. Reviewers
// must judge the proposed content, not the version still being served.
const effective = (a) => ({ ...a, ...(a.pending_payload || {}) });

// is_live (are readers seeing it?) and review_status (is a change outstanding?)
// are independent, which is what lets a live alert hold a pending edit.
function statusOf(a) {
  if (a.review_status === 'pending') {
    return a.is_live
      ? { label: 'Live · edit awaiting review', cls: 'bg-amber-100 text-amber-800' }
      : { label: 'Awaiting review', cls: 'bg-amber-100 text-amber-800' };
  }
  // A rejection leaves review_status 'draft' with the reviewer's note attached.
  // Surface it in the list — an author who only sees "Draft" has no way to know
  // their submission came back or why.
  if (a.review_note) {
    return a.is_live
      ? { label: 'Live · edit returned', cls: 'bg-red-100 text-red-700' }
      : { label: 'Returned for changes', cls: 'bg-red-100 text-red-700' };
  }
  if (a.is_live) {
    return a.pending_payload
      ? { label: 'Live · unsubmitted edit', cls: 'bg-blue-100 text-blue-700' }
      : { label: 'Live', cls: 'bg-green-100 text-green-700' };
  }
  return { label: 'Draft', cls: 'bg-gray-100 text-gray-600' };
}

const EMPTY_FORM = {
  source: 'ic3',
  psa_number: '',
  title: '',
  summary: '',
  full_text: '',
  source_url: '',
  source_pdf_filename: '',
  published_at: todayISO(),
  notify: true,
  // full_text is text box 1 (above the section image), know_more box 2 -
  // every source since 10-28. hero_caption is hot scam only.
  hero_caption: '',
  know_more: '',
};

const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';

// alertsLevel: none | read | edit | full (full adds delete).
// reviewLevel: none | preview | edit | full (full approves / rejects).
// Per-card permissions (10-07); the server enforces the same levels.
export default function AlertPublisher({ Section, alertsLevel = 'none', reviewLevel = 'none' }) {
  const canReadAlerts = alertsLevel !== 'none';
  const canEditAlerts = alertsLevel === 'edit' || alertsLevel === 'full';
  const canDelete = alertsLevel === 'full';
  const canSeeQueue = reviewLevel !== 'none';
  const canEditQueue = reviewLevel === 'edit' || reviewLevel === 'full';
  const canApprove = reviewLevel === 'full';
  // Who may open a given row in the editor.
  const canEditRow = (a) => canEditAlerts || (canEditQueue && a.review_status === 'pending');

  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null); // the alert row being edited
  const [alerts, setAlerts] = useState([]);
  const [listFilter, setListFilter] = useState('ic3');
  const [fullPreview, setFullPreview] = useState(null); // form-shaped object to preview
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [reviewMsg, setReviewMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadAlerts = useCallback(async () => {
    // Admin endpoint: every alert regardless of review state (the reader
    // endpoint is live-only, so it can't back a review queue).
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE_URL}/bff/admin/ic3/alerts?limit=300`, { credentials: 'include' }),
        fetch(`${API_BASE_URL}/bff/admin/hot-scams?limit=300`, { credentials: 'include' }),
      ]);
      const d1 = r1.ok ? await r1.json() : { items: [] };
      const d2 = r2.ok ? await r2.json() : { items: [] };
      // Hot scams are tagged so the shared queue / list / actions can tell
      // them apart; their id space is separate from the alerts'.
      const merged = [
        ...(d1.items || []),
        ...(d2.items || []).map((h) => ({ ...h, kind: HOT_SCAM, source: HOT_SCAM })),
      ];
      // The endpoints already order by publish date; sorting again keeps the
      // list right if that ever changes, and matches the reader hub's behaviour.
      setAlerts(merged.sort((a, b) => new Date(b.published_at) - new Date(a.published_at)));
    } catch { setAlerts([]); }
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const source = SOURCE_BY_ID[form.source];

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); };

  const insertTemplate = () => {
    const filled = !isEmptyHtml(form.full_text) || (source.template2 && !isEmptyHtml(form.know_more));
    if (filled && !window.confirm(source.template2 ? 'Replace both text boxes with the template?' : 'Replace the current body with the template?')) return;
    setField('full_text', source.template);
    if (source.template2) setField('know_more', source.template2);
  };

  const startEdit = (a) => {
    // Load the staged edit if there is one — otherwise you'd silently revert it.
    const e = effective(a);
    setEditing(a);
    setForm({
      source: e.source || 'ic3',
      psa_number: e.psa_number || '',
      title: e.title || '',
      summary: e.summary || '',
      // A hot scam's body column is what_to_know; the editor field is shared.
      full_text: (a.kind === HOT_SCAM ? e.what_to_know : e.full_text) || '',
      source_url: e.source_url || '',
      source_pdf_filename: e.source_pdf_filename || '',
      published_at: e.published_at ? e.published_at.slice(0, 10) : todayISO(),
      notify: false, // edits default silent
      hero_caption: e.hero_caption || '',
      know_more: e.know_more || '',
    });
    setMsg(null);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setParsing(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/ic3/parse-pdf`, { method: 'POST', body: fd });
      if (!r.ok) { setMsg({ type: 'error', text: 'PDF parse failed' }); return; }
      const d = await r.json();
      setForm((f) => ({
        ...f,
        title: d.suggestedTitle || f.title,
        summary: d.suggestedSummary || f.summary,
        psa_number: d.alertNumber ? d.alertNumber.replace(/^alert number:?\s*/i, '') : f.psa_number,
        source_url: d.suggestedSourceUrl || f.source_url,
        source_pdf_filename: d.filename || '',
      }));
      setMsg({ type: 'ok', text: `Parsed ${d.filename} — review + edit, then submit.` });
    } catch {
      setMsg({ type: 'error', text: 'Network error' });
    } finally {
      setParsing(false);
    }
  };

  // submitForReview: save, then move it into the queue in one action.
  const save = async ({ submit }) => {
    setSaving(true);
    setMsg(null);
    try {
      const bodyHtml = isEmptyHtml(form.full_text) ? null : trimTrailingEmpty(form.full_text);
      const publishedAt = form.published_at ? `${form.published_at}T00:00:00Z` : null;
      const body = isHotScam
        ? {
            title: form.title.trim(),
            what_to_know: bodyHtml,
            know_more: isEmptyHtml(form.know_more) ? null : trimTrailingEmpty(form.know_more),
            hero_caption: form.hero_caption.trim() || null,
            published_at: publishedAt,
            submit,
          }
        : {
            source: form.source,
            psa_number: form.psa_number.trim() || null,
            title: form.title.trim(),
            summary: form.summary.trim() || null,
            full_text: bodyHtml,
            know_more: isEmptyHtml(form.know_more) ? null : trimTrailingEmpty(form.know_more),
            source_url: form.source_url.trim() || null,
            published_at: publishedAt,
            submit,
          };
      const base = `${API_BASE_URL}/bff/${adminPath(isHotScam ? HOT_SCAM : 'ic3')}`;
      const r = editing
        ? await fetchWithCsrf(`${base}/${editing.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
        : await fetchWithCsrf(base, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...body,
              ...(isHotScam ? {} : { source_pdf_filename: form.source_pdf_filename || null }),
              notify: form.notify,
            }),
          });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setMsg({ type: 'error', text: err.errors?.[0]?.msg || err.message || err.error || 'Failed to save' });
        return;
      }
      setMsg({
        type: 'ok',
        text: submit
          ? 'Submitted for review — a reviewer has to approve it before readers see it.'
          : 'Saved as a draft. Nothing is visible to readers yet.',
      });
      resetForm();
      loadAlerts();
    } catch {
      setMsg({ type: 'error', text: 'Network error' });
    } finally {
      setSaving(false);
    }
  };

  const review = async (a, action) => {
    let note = null;
    if (action === 'reject') {
      note = window.prompt(`Send it back to the author. What needs to change?\n\n"${effective(a).title}"`);
      if (note === null) return; // cancelled
    } else if (!window.confirm(
      `Approve and release:\n\n"${effective(a).title}"\n\n${
        a.is_live ? 'The staged edit replaces the live version.' : 'This makes the alert visible to all readers.'
      }${a.notify_on_publish ? '\nEvery user gets a notification.' : ''}`
    )) return;

    setBusyId(a.id);
    setReviewMsg(null);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/${adminPath(a)}/${a.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject' ? { note: note || null } : {}),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setReviewMsg({ type: 'error', text: err.message || err.error || 'Failed' });
        return;
      }
      setReviewMsg({
        type: 'ok',
        text: action === 'approve' ? 'Approved and released.' : 'Sent back to the author.',
      });
      if (editing?.id === a.id && rowKind(editing) === rowKind(a)) resetForm();
      loadAlerts();
    } catch {
      setReviewMsg({ type: 'error', text: 'Network error' });
    } finally {
      setBusyId(null);
    }
  };

  const deleteAlert = async (a) => {
    const noun = a.kind === HOT_SCAM ? 'hot scam page' : 'alert';
    if (!window.confirm(
      a.is_live
        ? `Delete the LIVE ${noun} "${a.title}"? Readers will stop seeing it immediately.`
        : `Delete the draft ${noun} "${a.title || 'Untitled'}"?`
    )) return;
    setAlerts((list) => list.filter((x) => !(x.id === a.id && rowKind(x) === rowKind(a))));
    if (editing?.id === a.id && rowKind(editing) === rowKind(a)) resetForm();
    try { await fetchWithCsrf(`${API_BASE_URL}/bff/${adminPath(a)}/${a.id}`, { method: 'DELETE' }); } catch { /* ignore */ }
  };

  const isHotScam = form.source === HOT_SCAM;
  const pending = alerts.filter((a) => a.review_status === 'pending');
  const visibleAlerts = alerts.filter((a) => rowKind(a) === listFilter);
  const editingLive = !!editing?.is_live;
  const canSubmit = !!form.title.trim();

  return (
    <>
      {/* ------------------------------------------------ review queue */}
      {canSeeQueue && (
      <Section
        title={`Review queue (${pending.length})`}
        right={reviewMsg && (
          <span className={`text-sm ${reviewMsg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
            {reviewMsg.text}
          </span>
        )}
      >
        <p className="text-xs text-gray-400 mb-3">
          {canApprove
            ? 'Alerts waiting on release. Approving publishes the alert to every reader; rejecting returns it to the author with your note and leaves any live version untouched.'
            : 'Alerts waiting on a reviewer to release them. A submission can be edited until it is approved.'}
        </p>
        {pending.length === 0 ? (
          <p className="text-gray-400 text-sm">Nothing awaiting review.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {pending.map((a) => {
              const e = effective(a);
              return (
                <div key={`${rowKind(a)}-${a.id}`} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-black">{e.title || 'Untitled alert'}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        <span className="uppercase font-semibold">{SOURCE_BY_ID[e.source]?.label || e.source}</span>
                        {e.psa_number ? ` · ${e.psa_number}` : ''}
                        {e.published_at ? ` · ${fmtDate(e.published_at)}` : ''}
                        {a.submitted_by_name || a.submitted_by_email
                          ? ` · submitted by ${a.submitted_by_name || a.submitted_by_email}`
                          : ''}
                        {a.submitted_at ? ` · ${fmtWhen(a.submitted_at)}` : ''}
                      </p>
                      {a.is_live && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          Edit to a live alert — readers keep seeing the approved version until this is released.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        onClick={() => setFullPreview(a.kind === HOT_SCAM
                          ? { hotScam: e }
                          : { alert: e })}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Preview
                      </button>
                      {canEditRow(a) && (
                        <button onClick={() => startEdit(a)} className="text-xs text-blue-600 hover:underline">Edit</button>
                      )}
                      {canApprove ? (
                        <>
                          <button
                            onClick={() => review(a, 'approve')}
                            disabled={busyId === a.id}
                            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            Approve &amp; release
                          </button>
                          <button
                            onClick={() => review(a, 'reject')}
                            disabled={busyId === a.id}
                            className="text-xs text-red-600 hover:underline disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="text-[11px] text-gray-400">awaiting review</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
      )}

      {/* ------------------------------------------------ editor */}
      {(canEditAlerts || (editing && canEditQueue)) && (
      <Section title={editing ? `Edit ${editing.kind === HOT_SCAM ? 'ScamWire page' : 'Alert'} #${editing.id}` : 'New CyberFraud Alert'}>
        {editingLive && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            This alert is <b>live</b>. Your changes are staged and need approval — readers keep seeing the
            currently approved version until a reviewer releases the edit.
          </p>
        )}
        {editing?.review_note && (
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
            <b>Returned by {editing.reviewed_by_name || editing.reviewed_by_email || 'a reviewer'}:</b>{' '}
            {editing.review_note}
          </p>
        )}
        <form onSubmit={(e) => { e.preventDefault(); save({ submit: true }); }} className="space-y-3">
          {/* Source selector */}
          <div className="flex items-center gap-2 flex-wrap">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setField('source', s.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  form.source === s.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
            {form.source === 'ic3' && !editing && (
              <label className={`ml-auto inline-flex items-center gap-2 text-sm px-3 py-1.5 border border-dashed border-blue-300 rounded-lg cursor-pointer ${parsing ? 'opacity-50' : 'text-blue-600 hover:bg-blue-50'}`}>
                {parsing ? 'Parsing PDF…' : 'Upload IC3 PDF to pre-fill'}
                <input type="file" accept="application/pdf" onChange={onPdf} className="hidden" disabled={parsing} />
              </label>
            )}
          </div>

          <input className={input} placeholder={isHotScam ? 'Page title * (e.g. Pig Butchering / Crypto)' : 'Title *'} value={form.title} onChange={(e) => setField('title', e.target.value)} required />

          <div className="flex gap-3 flex-wrap">
            {source.refLabel && (
              <input
                className={`${input} max-w-[240px]`}
                placeholder={source.refPlaceholder}
                title={source.refLabel}
                value={form.psa_number}
                onChange={(e) => setField('psa_number', e.target.value)}
              />
            )}
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Publish date *
              <input
                type="date"
                className={`${input} w-auto`}
                value={form.published_at}
                onChange={(e) => setField('published_at', e.target.value)}
                required
              />
            </label>
          </div>

          {!isHotScam && (
            <>
              <input className={input} placeholder={`Source URL (${source.urlPlaceholder})`} value={form.source_url} onChange={(e) => setField('source_url', e.target.value)} />
              <input className={input} placeholder="Summary — one or two sentences (shown in notifications)" value={form.summary} onChange={(e) => setField('summary', e.target.value)} />
              <p className="text-xs text-gray-400">
                The page is the framed header (agency seal and name, reference, date), text box 1, the agency&rsquo;s
                section image, text box 2 and the agency User Notice — the same layout as a ScamWire page.
              </p>
            </>
          )}

          {isHotScam && (
            <>
              <input className={input} placeholder="Hero image caption (e.g. FBI / IC3 2025 Annual Report)" value={form.hero_caption} onChange={(e) => setField('hero_caption', e.target.value)} />
              <p className="text-xs text-gray-400">
                The page is the framed hero, text box 1, the section image, text box 2. Headings and lists are yours to
                write (the template lays out the 01 – 06 sections the existing pages use). Images: the shared hot-scam
                artwork is used for every page for now.
              </p>
            </>
          )}

          {/* Body editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-400">Text box 1 — above the image</span>
              <div className="flex gap-3">
                <button type="button" onClick={insertTemplate} className="text-xs text-blue-600 hover:underline">
                  Insert {source.label} template
                </button>
                <button
                  type="button"
                  onClick={() => setFullPreview(isHotScam ? { hotScam: hotScamPreviewShape(form) } : { alert: alertPreviewShape(form) })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Full page preview
                </button>
              </div>
            </div>
            <EditorBoundary>
              <Suspense
                fallback={(
                  <div className="border border-gray-200 rounded-lg min-h-[340px] bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                    Loading editor…
                  </div>
                )}
              >
                <AlertEditor
                  value={form.full_text}
                  onChange={(html) => setField('full_text', html)}
                  placeholder="Text above the image…"
                />
              </Suspense>
            </EditorBoundary>
          </div>

          {/* Second box for every source (10-28). */}
          <div>
              <span className="text-xs text-gray-400">Text box 2 — below the image</span>
              <div className="mt-1">
                <EditorBoundary>
                  <Suspense
                    fallback={(
                      <div className="border border-gray-200 rounded-lg min-h-[340px] bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                        Loading editor…
                      </div>
                    )}
                  >
                    <AlertEditor
                      value={form.know_more}
                      onChange={(html) => setField('know_more', html)}
                      placeholder="Text below the image — red flags, attack mode, prevention tips…"
                    />
                  </Suspense>
                </EditorBoundary>
              </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <button type="submit" disabled={saving || !canSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Saving…' : 'Submit for review'}
            </button>
            <button
              type="button"
              onClick={() => save({ submit: false })}
              disabled={saving || !canSubmit}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
            >
              {editingLive ? 'Save staged edit' : 'Save draft'}
            </button>
            {!editing && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={form.notify} onChange={(e) => setField('notify', e.target.checked)} />
                Notify all users on release
              </label>
            )}
            {editing && (
              <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:underline">
                Cancel edit
              </button>
            )}
            {msg && <span className={`text-sm ${msg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{msg.text}</span>}
          </div>
        </form>
      </Section>
      )}

      {fullPreview?.hotScam && (
        <HotScamPreviewModal scam={fullPreview.hotScam} onClose={() => setFullPreview(null)} />
      )}
      {fullPreview?.alert && (
        <AlertPreviewModal alert={fullPreview.alert} onClose={() => setFullPreview(null)} />
      )}

      {canReadAlerts && (
      <Section
        title={`Alerts (${visibleAlerts.length})`}
        right={(
          <div className="flex gap-1">
            {SOURCES.map((s) => s.id).map((id) => (
              <button
                key={id}
                onClick={() => setListFilter(id)}
                title={id === HOT_SCAM ? 'ScamWire' : undefined}
                className={`px-2 py-1 rounded text-xs ${
                  listFilter === id ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {id === HOT_SCAM ? 'ScamWire' : SOURCE_BY_ID[id].label}
              </button>
            ))}
          </div>
        )}
      >
        {visibleAlerts.length === 0 ? (
          <p className="text-gray-400 text-sm">None yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleAlerts.map((a) => {
              const status = statusOf(a);
              return (
                <div key={`${rowKind(a)}-${a.id}`} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-black truncate">{a.title}</p>
                    <p className="text-[11px] text-gray-400">
                      <span className={`px-1.5 py-0.5 rounded font-medium mr-1.5 ${status.cls}`}>{status.label}</span>
                      {a.psa_number ? `${a.psa_number} · ` : ''}
                      {a.published_at ? `${fmtDate(a.published_at)} · ` : ''}
                      {a.kind === HOT_SCAM ? `/dashboard/hot-scams/${a.slug}` : (a.source_url ? 'linked' : 'no link')}
                      {a.reviewed_by_name && a.review_status === 'published'
                        ? ` · released by ${a.reviewed_by_name}`
                        : ''}
                    </p>
                    {a.review_note && a.review_status === 'draft' && (
                      <p className="text-[11px] text-red-700 truncate mt-0.5">
                        {a.reviewed_by_name ? `${a.reviewed_by_name}: ` : ''}{a.review_note}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    {canEditRow(a) && (
                      <button onClick={() => startEdit(a)} className="text-xs text-blue-600 hover:underline">Edit</button>
                    )}
                    {canDelete && (
                      <button onClick={() => deleteAlert(a)} className="text-xs text-red-600 hover:underline">Delete</button>
                    )}
                    {!canEditRow(a) && !canDelete && (
                      <span className="text-[11px] text-gray-300">read only</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
      )}
    </>
  );
}
